import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  CreateSavedFilterDocument,
  DeleteSavedFilterDocument,
  SavedFiltersDocument,
  UpdateSavedFilterDocument,
  resultErrorMessage,
  unwrapResult,
  type CreateSavedFilterInput,
  type SavedFilterRow,
  type UpdateSavedFilterInput,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";

const EMPTY_SAVED_FILTERS: SavedFilterRow[] = [];

export type UseSavedFiltersResult = {
  /** The viewer's saved filters for this category, by name. */
  savedFilters: SavedFilterRow[];
  createSavedFilter: (
    input: Omit<CreateSavedFilterInput, "category">,
  ) => Promise<SavedFilterRow | null>;
  /** Rename, or re-snapshot with new states — each field travels alone. */
  updateSavedFilter: (
    id: string,
    input: UpdateSavedFilterInput,
  ) => Promise<SavedFilterRow | null>;
  deleteSavedFilter: (id: string) => Promise<boolean>;
};

/**
 * THE saved-filter store for one filter category — the Saved tab of the
 * toolbar panel and of the Customize view Filters page. Rows are PERSONAL:
 * the server answers the current member's
 * own and nobody else's, so nothing here checks a permission.
 *
 * The list is a plain array field, not a connection, so a create is appended
 * to the cached query and a delete is filtered out of it — normalization only
 * refreshes rows already listed.
 */
export function useSavedFilters(category: string): UseSavedFiltersResult {
  const variables = useMemo(() => ({ category }), [category]);
  const { data } = useQuery(SavedFiltersDocument, { variables });
  const [createMutation] = useMutation(CreateSavedFilterDocument);
  const [updateMutation] = useMutation(UpdateSavedFilterDocument);
  const [deleteMutation] = useMutation(DeleteSavedFilterDocument);

  const savedFilters = data?.savedFilters ?? EMPTY_SAVED_FILTERS;

  const createSavedFilter = useCallback(
    async (input: Omit<CreateSavedFilterInput, "category">) => {
      try {
        const { data: result } = await createMutation({
          variables: { input: { ...input, category } },
          update: (cache, { data: payload }) => {
            const created = payload?.createSavedFilter;
            if (created?.__typename !== "SavedFilter") return;
            cache.updateQuery(
              { query: SavedFiltersDocument, variables },
              (prev) =>
                prev
                  ? {
                      ...prev,
                      savedFilters: [...prev.savedFilters, created].sort((a, b) =>
                        a.name.localeCompare(b.name),
                      ),
                    }
                  : prev,
            );
          },
        });
        const unwrapped = unwrapResult(result?.createSavedFilter);
        if (!unwrapped.ok) {
          toastManager.add({
            title: resultErrorMessage(unwrapped.error, "Failed to save filter"),
            type: "error",
          });
          return null;
        }
        return unwrapped.data;
      } catch (err) {
        toastMutationError(err, "Failed to save filter");
        return null;
      }
    },
    [category, createMutation, variables],
  );

  // A rename changes the list order, which the cached array does not
  // re-sort on a normalized update — so the row is written back into the
  // query in its new place.
  const updateSavedFilter = useCallback(
    async (id: string, input: UpdateSavedFilterInput) => {
      try {
        const { data: result } = await updateMutation({
          variables: { id, input },
          update: (cache, { data: payload }) => {
            const updated = payload?.updateSavedFilter;
            if (!updated) return;
            cache.updateQuery(
              { query: SavedFiltersDocument, variables },
              (prev) =>
                prev
                  ? {
                      ...prev,
                      savedFilters: prev.savedFilters
                        .map((row) => (row.id === id ? updated : row))
                        .sort((a, b) => a.name.localeCompare(b.name)),
                    }
                  : prev,
            );
          },
        });
        return result?.updateSavedFilter ?? null;
      } catch (err) {
        toastMutationError(err, "Failed to update saved filter");
        return null;
      }
    },
    [updateMutation, variables],
  );

  const deleteSavedFilter = useCallback(
    async (id: string) => {
      try {
        const { data: result } = await deleteMutation({
          variables: { id },
          update: (cache, { data: payload }) => {
            if (!payload?.deleteSavedFilter) return;
            cache.updateQuery(
              { query: SavedFiltersDocument, variables },
              (prev) =>
                prev
                  ? {
                      ...prev,
                      savedFilters: prev.savedFilters.filter((row) => row.id !== id),
                    }
                  : prev,
            );
            const cacheId = cache.identify({ __typename: "SavedFilter", id });
            // An absent id reads as ROOT, which would evict a root field.
            if (cacheId) cache.evict({ id: cacheId });
            cache.gc();
          },
        });
        return result?.deleteSavedFilter != null;
      } catch (err) {
        toastMutationError(err, "Failed to delete saved filter");
        return false;
      }
    },
    [deleteMutation, variables],
  );

  return useMemo(
    () => ({ savedFilters, createSavedFilter, updateSavedFilter, deleteSavedFilter }),
    [savedFilters, createSavedFilter, updateSavedFilter, deleteSavedFilter],
  );
}
